import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Cog, Layers, Factory, Zap, Wrench, Search, Package, Boxes, Hammer, Flame, Droplet } from "lucide-react";
import Seo, { ORG_LD } from "@/components/site/Seo";
import { RevealLines } from "@/components/site/RevealText";

const GROUPS = [
  {
    icon: Cog,
    title: "Industrial Equipment & Components",
    img: "/categories/industrial-equipment.jpg",
    items: [
      "Pumps (centrifugal, positive displacement, submersible)",
      "Valves (gate, globe, ball, butterfly, check, control)",
      "Compressors and blowers",
      "Heat exchangers",
      "Mechanical drives, gearboxes, couplings",
      "Filtration and separation equipment",
    ],
  },
  {
    icon: Layers,
    title: "Metals, Tubes & Engineered Materials",
    img: "/categories/metals-tubes.jpg",
    items: [
      "Stainless steel plates, sheets, rods, wire",
      "Nickel alloys (Inconel, Monel, Hastelloy)",
      "Duplex and super-duplex materials",
      "Seamless and welded tubes, pipes and fittings",
      "Flanges, forgings and machined blanks",
      "Specialty aluminum, copper and titanium",
    ],
  },
  {
    icon: Factory,
    title: "Oil & Gas Supply",
    img: "/categories/oil-gas.jpg",
    items: [
      "Wellhead and Christmas tree components",
      "Flow iron, chokes and manifolds",
      "OCTG tubing and casing",
      "Line pipe, coated spools and induction bends",
      "Downstream valves and pressure equipment",
      "MRO consumables for rigs and platforms",
    ],
  },
  {
    icon: Zap,
    title: "Electrical & Mechanical Components",
    img: "/categories/electrical-mechanical.jpg",
    items: [
      "LV / MV switchgear and panels",
      "Motors, drives and starters",
      "Cable, glands and terminations",
      "Instrumentation (pressure, flow, level, temperature)",
      "Enclosures, junction boxes and cabinets",
      "Bearings, seals and drive components",
    ],
  },
  {
    icon: Wrench,
    title: "Custom Manufacturing",
    img: "/categories/custom-manufacturing.jpg",
    items: [
      "CNC machined parts from drawings or samples",
      "Sheet metal fabrication and enclosures",
      "Forged and cast components",
      "Injection-moulded plastics and rubber parts",
      "Welded assemblies and skids",
      "Contract assembly and box-build",
    ],
  },
  {
    icon: Search,
    title: "Hard-to-Find & Obsolete Parts",
    img: "/categories/hard-to-find.jpg",
    items: [
      "Discontinued OEM components",
      "Long-lead spares for shutdowns and turnarounds",
      "Legacy control and instrumentation hardware",
      "Non-catalogued fasteners and fittings",
      "Reverse-engineered replacements from samples",
      "Cross-referenced equivalents to OEM P/N",
    ],
  },
  {
    icon: Hammer,
    title: "Forgings",
    img: "/categories/forgings.jpg",
    items: [
      "Closed-die forgings (flanges, connectors, wellhead bodies)",
      "Open-die forgings (shafts, blocks, discs, hollow forgings)",
      "Ring rolling (Ø 200-3,000 mm, seamless rolled rings)",
      "Radial and precision forgings",
      "Carbon, alloy, stainless, Inconel and Duplex",
      "API 6A PSL-3, ASTM A105/A182/A350, NACE MR0175",
    ],
  },
  {
    icon: Flame,
    title: "Castings",
    img: "/categories/castings.jpg",
    items: [
      "Sand castings (up to 10,000 kg, iron / steel / bronze)",
      "Investment castings (0.02-100 kg, tight tolerances)",
      "Shell mould and centrifugal castings",
      "Materials: WCB / WCC, CF8M, CD4MCu, Inconel, Al A356",
      "Machined-to-print secondary operations",
      "RT / UT / MT / PT / hardness / spectro testing",
    ],
  },
  {
    icon: Droplet,
    title: "Injection Molding",
    img: "/categories/injection-molding.jpg",
    items: [
      "Plastic Injection Moulding (PIM) — 50-1,600 t clamping presses",
      "Metal Injection Moulding (MIM) — complex small precision parts",
      "Insert moulding and multi-material / overmoulding",
      "Materials: ABS, PC, PP, POM, Nylon (GF), PEEK, PPS, TPE, LSR",
      "1-64 cavity tooling with hot- and cold-runner systems",
      "DFM, mould-flow simulation, PPAP L3/L4",
    ],
  },
];


export default function WhatWeSource() {
  return (
    <div className="bg-nx-bg text-nx-ink" data-testid="page-what-we-source">
      <Seo
        title="What We Source · Industrial Equipment, Metals, Oil & Gas, Custom Manufacturing"
        description="Six broad sourcing categories: industrial equipment & components, metals & engineered materials, oil & gas supply, electrical & mechanical components, custom manufacturing, and hard-to-find or obsolete parts."
        path="/what-we-source"
        structuredData={ORG_LD}
      />
      <section className="bg-nx-navydark text-nx-bg px-6 md:px-12 lg:px-20 pt-32 md:pt-40 pb-24">
        <div className="font-mono text-[0.65rem] uppercase tracking-[0.28em] text-nx-copper mb-6">What We Source</div>
        <RevealLines
          as="h1"
          lines={[<>If it is industrial,</>, <>we can probably source it.</>]}
          className="font-cabinet font-black tracking-[-0.04em] leading-[0.92] text-[13vw] md:text-[7vw] lg:text-[5.4vw] text-nx-bg"
        />
        <p className="mt-8 max-w-2xl text-nx-bg/75 leading-relaxed text-lg">
          Nine broad categories, one accountable partner. The list below is illustrative — if your
          requirement is adjacent to any of it, we can almost certainly help.
        </p>
        <div className="mt-10">
          <Link to="/sourcing-request" className="nx-btn on-dark" data-testid="wws-cta">
            Submit a Sourcing Requirement <ArrowUpRight size={14} />
          </Link>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-20 py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-nx-navy/20 border border-nx-navy/25">
          {GROUPS.map((g, i) => (
            <motion.article
              key={g.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10%" }}
              transition={{ duration: 0.6, delay: i * 0.05 }}
              className="bg-nx-bg overflow-hidden"
              data-testid={`wws-group-${i}`}
            >
              <div className="relative aspect-[16/9] overflow-hidden bg-nx-navy">
                <img
                  src={g.img}
                  alt={g.title}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-nx-bg/70 via-transparent to-transparent" />
                <span className="absolute top-4 left-4 font-mono text-[0.6rem] uppercase tracking-[0.24em] bg-nx-navy text-nx-bg px-2 py-1">
                  Category {String(i + 1).padStart(2, "0")}
                </span>
              </div>
              <div className="p-8 md:p-10">
                <div className="flex items-center gap-3 mb-6">
                  <g.icon size={22} className="text-nx-copper" />
                  <span className="font-mono text-[0.6rem] uppercase tracking-[0.24em] text-nx-copper">Sourcing scope</span>
                </div>
                <h2 className="font-cabinet font-black text-2xl md:text-3xl tracking-tight text-nx-navy leading-tight">{g.title}</h2>
                <ul className="mt-6 space-y-3">
                  {g.items.map((item) => (
                    <li key={item} className="grid grid-cols-[auto_1fr] gap-3 text-nx-navy2 text-sm leading-relaxed">
                      <span className="text-nx-copper mt-0.5">→</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      <section className="bg-nx-navydark text-nx-bg px-6 md:px-12 lg:px-20 py-24 md:py-28" data-testid="wws-cta-bottom">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <h2 className="font-cabinet font-black tracking-[-0.035em] leading-[0.95] text-[10vw] md:text-[5vw] text-nx-bg">
              Not on the list? <span className="text-nx-copper">Send it anyway.</span>
            </h2>
            <p className="mt-6 text-nx-bg/75 max-w-xl leading-relaxed">
              Our best work is often on requirements that don't fit a category. Describe what you need
              — a drawing, a part number, or a paragraph — and we'll tell you if and how we can help.
            </p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link to="/sourcing-request" className="nx-btn on-dark" data-testid="wws-cta-bottom-btn">
              Submit a Requirement <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
